// @ts-check
// Orquestación de sesión del top-nav. Nació para no triplicar la lógica de
// logout/redirección entre `shell-nav.js`, `pseudocode-shell-nav.js` y
// `python-shell-nav.js`; desde que esos dos últimos se unificaron en
// `shell-nav.js` (2026-08-05) solo queda un consumidor, pero el módulo se
// conserva: separa la orquestación de sesión del montaje de la barra y es el
// punto donde vive el guardado de borradores al cerrar sesión.
// Vive en la capa "pages"
// (docs/03-frontend.md §4.3): SÍ puede orquestar sesión (`state/session-store.js`)
// y navegación, a diferencia del componente puro `components/session-menu.js`,
// que solo pinta la pastilla + botón y recibe el logout como callback opaco.
import { crearMenuSesion } from '../components/session-menu.js';
import { cerrarSesion, obtenerUsuario } from '../state/session-store.js';
import { guardarBorradoresDeUsuario } from '../state/editor-drafts.js';

/**
 * Redirección real a `login.html` tras cerrar sesión. Aislada para poder
 * inyectarla en tests (jsdom no implementa la navegación de documento
 * completo), mismo patrón que `diagram-editor-page.js#redirigirALogin`.
 */
export function redirigirALogin() {
  if (typeof window !== 'undefined') {
    window.location.assign('login.html');
  }
}

/**
 * Orquesta el cierre de sesión desde el top-nav: llama a `cerrarSesion()`
 * (`state/session-store.js`, ya gestiona el token CSRF) y, tanto si tiene
 * éxito como si la red falla, SIEMPRE redirige a `login.html`. Mismo criterio
 * documentado en `diagram-editor-page.js#manejarCerrarSesion`: un fallo de red
 * en el logout no deja la UI en un estado ambiguo -- ante la duda, forzar
 * re-login. Ningún error se registra en consola (datos de sesión).
 * @param {() => void} redirigir
 * @returns {Promise<void>}
 */
export async function manejarCerrarSesion(redirigir) {
  // Se captura el usuario ANTES de `cerrarSesion()` (que vacía la sesión en
  // memoria): al salir se GUARDA su borrador de trabajo bajo su id, para que lo
  // recupere la próxima vez que inicie sesión (ver `state/editor-drafts.js`).
  const usuario = obtenerUsuario();
  try {
    await cerrarSesion();
  } catch {
    // Fallo de red/servidor al cerrar sesión: se ignora a propósito. Se
    // redirige igual en el `finally`.
  } finally {
    // Guarda el trabajo del usuario que sale y deja las claves de trabajo
    // limpias para el siguiente usuario del navegador. Sin id, solo aísla.
    guardarBorradoresDeUsuario(usuario?.id);
    redirigir();
  }
}

/**
 * Construye la pastilla de usuario + botón "Cerrar sesión" ya cableados al
 * logout, listos para insertarse en el `slotSesion` del top-nav. El
 * componente `session-menu.js` sigue siendo PURO: la orquestación (llamar a
 * `cerrarSesion()` + redirigir) se inyecta aquí.
 * @param {{ nombreVisible: string, rol: string }} usuario
 * @param {() => void} redirigir
 * @returns {ReturnType<typeof crearMenuSesion>}
 */
export function construirMenuSesionNav(usuario, redirigir) {
  return crearMenuSesion({
    usuario,
    onCerrarSesion: () => manejarCerrarSesion(redirigir),
  });
}
